import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Field, Form, Input, TextArea, Typography } from '@/components';
import { nullableStringSetter } from '@/helpers';
import { OpportunityDetails } from '../../../types';
import { useSaleOptions } from '@/hooks';
import { NUMERIC } from '@/features/projects/helpers/numeric-inputs';

export type SaleProcessFormProps = {
  readonly id: string;
  readonly opportunity: OpportunityDetails;
  readonly onSubmit: (data: any) => void;
};

export function SaleProcessFormNew({ id, opportunity, onSubmit }: SaleProcessFormProps) {
  const { t } = useTranslation(['opportunities']);

  const { options: mapSaleOption } = useSaleOptions();

  const options = useMemo(
    () => ({
      defaultValues: {
        minimum_percentage: opportunity.percentage.minimum,
        maximum_percentage: opportunity.percentage.maximum,
        options: opportunity.options,
        comments: opportunity.comments ?? null,
      },
    }),
    [opportunity]
  );

  return (
    <Form
      id={id}
      onSubmit={(data) => {
        onSubmit({
          minimum_percentage: data.minimum_percentage,
          maximum_percentage: data.maximum_percentage,
          options: data.options,
          comments: data.comments ?? null,
        });
      }}
      options={options}
    >
      {({ formState, register }) => (
        <div className="space-y-6">
          <Typography as="p" color="secondary" size="body-3" weight="normal" className="mt-1 italic">
            {t(
              'Please, fill in this section with all the key details of the sale process for your desired transaction. Include relevant information to guide potential buyers smoothly through each step of the transaction.'
            )}
          </Typography>
          <Field id="minimum_percentage" label={t('Percentage range')} delay={0.3} classes={{ field: 'space-y-4' }}>
            <div className="flex w-full items-center">
              <div className="">
                <Input
                  id="minimum_percentage"
                  placeholder="0"
                  color={formState.errors['minimum_percentage'] ? 'error' : 'primary'}
                  registration={register('minimum_percentage', {
                    required: t('This field is required'),
                    min: 0,
                    max: 100,
                    ...NUMERIC
                  })}
                  type="number"
                  suffix="%"
                />
              </div>
              <span className="px-3">-</span>
              <div className="">
                <Input
                  id="maximum_percentage"
                  placeholder="100"
                  color={formState.errors['maximum_percentage'] ? 'error' : 'primary'}
                  registration={register('maximum_percentage', {
                    required: t('This field is required'),
                    min: 0,
                    max: 100,
                    ...NUMERIC,
                  })}
                  type="number"
                  suffix="%"
                />
              </div>
            </div>
          </Field>

          <Field label={t('Deal Type')} delay={0.45} classes={{ field: 'space-y-4', input: 'flex flex-wrap gap-3' }}>
            {mapSaleOption.map((option) => (
              <Checkbox color="primary" registration={register('options')} value={option.value}>
                {option.label}
              </Checkbox>
            ))}
          </Field>

          <Field
            id="comments"
            label={t('Process details')}
            error={formState.errors['comments']}
            delay={0.6}
            classes={{ field: 'space-y-4' }}
          >
            <TextArea
              id="comments"
              color="primary"
              placeholder="Type here..."
              registration={register('comments', { setValueAs: nullableStringSetter })}
            />
          </Field>
        </div>
      )}
    </Form>
  );
}
